import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { inrFull } from '../data';
import type { StatutoryClose } from '../types';

/**
 * The control account and the month grid — what an accountant opens first.
 *
 * WHY THIS SITS ABOVE THE FINDINGS LIST
 * A findings register is keyed A1..F2 because that is how the problems were found. An accountant
 * reads a payable account as a continuity schedule — opening + provisioned − paid = closing — and
 * works a month at a time. That single line is what exposed `ESIC Payable` closing at −2,14,528:
 * a liability in debit, invisible in a year of totals and impossible to miss here.
 *
 * A ROW IS A WAGE MONTH. Its provision is raised that month; its payment lands the NEXT month
 * (May wages are paid in June). The header says so, because a reader who assumes both come from
 * the same column will conclude every month is broken.
 */

const LINE_LABEL: Record<string, string> = {
	PF: 'PF',
	PF_EDLI_ADMIN: 'PF EDLI + Admin',
	ESIC: 'ESIC',
	PT: 'PT',
};

/**
 * Cell vocabulary. `no_activity` is a dash, never a tick and never a cross — "we have not seen
 * anything here" is a different statement from "this is fine" and from "this failed", and
 * collapsing the three is how a screen loses an accountant on day one.
 */
const CELL: Record<string, { label: string; cls: string; title: string }> = {
	tied: {
		label: 'tied',
		cls: 'bg-green-50 text-green-800 border-green-200',
		title: 'The challan and the payment booked to this account agree',
	},
	attention: {
		label: 'look',
		cls: 'bg-amber-50 text-amber-800 border-amber-200',
		title: 'The totals agree but an open finding says something is still wrong',
	},
	unpaid: {
		label: 'unpaid',
		cls: 'bg-red-50 text-red-800 border-red-200',
		title: 'A challan exists and there is no evidence the department was ever paid',
	},
	/**
	 * The status that only exists because the challan leg exists.
	 *
	 * Before the challans loaded, May and June 2025 PF both showed "unpaid" — because this
	 * account was never debited. EPFO had the money the whole time; the entry had gone to ESIC
	 * Payable. Telling an accountant a paid liability is unpaid is how it gets paid twice, so
	 * this gets its own colour and its own sentence.
	 */
	paid_elsewhere: {
		label: 'paid, wrong a/c',
		cls: 'bg-violet-50 text-violet-800 border-violet-200',
		title: 'The department was paid — the challan proves it — but this account was never debited',
	},
	paid_late: {
		label: 'paid late',
		cls: 'bg-yellow-50 text-yellow-800 border-yellow-200',
		title: 'The department was paid, but in a later month than the one the dues belong to',
	},
	no_challan: {
		label: 'no challan',
		cls: 'bg-sky-50 text-sky-800 border-sky-200',
		title: 'Money left the bank but no challan for this month is on file',
	},
	differs: {
		label: 'differs',
		cls: 'bg-orange-50 text-orange-800 border-orange-200',
		title: 'The challan and the booked payment do not agree — usually arrears catching up',
	},
	no_activity: {
		label: '—',
		cls: 'bg-white text-gray-300 border-gray-100',
		title: 'No provision and no payment for this month',
	},
	no_account: {
		label: 'no a/c',
		cls: 'bg-gray-50 text-gray-400 border-gray-200',
		title: 'There is no such account in Zoho, so this line cannot be reconciled at all (finding A9)',
	},
};

export const StatutoryCloseView: React.FC<{
	close: StatutoryClose;
	onFinding: (id: number) => void;
}> = ({ close, onFinding }) => (
	<div className='space-y-6'>
		<section>
			<div className='flex items-baseline justify-between mb-2'>
				<h3 className='text-sm font-medium text-gray-700'>Control accounts · FY {close.fy}</h3>
				<span className='text-xs text-gray-400'>opening + provisioned − paid = closing</span>
			</div>
			<div className='overflow-x-auto border border-gray-200 rounded-lg'>
				<table className='w-full text-sm'>
					<thead className='bg-gray-50 text-gray-500'>
						<tr>
							<th className='text-left px-3 py-2 font-medium'>Account</th>
							<th className='text-right px-3 py-2 font-medium'>Opening</th>
							<th className='text-right px-3 py-2 font-medium'>Provisioned</th>
							<th className='text-right px-3 py-2 font-medium'>Paid</th>
							<th className='text-right px-3 py-2 font-medium'>Closing</th>
						</tr>
					</thead>
					<tbody className='divide-y divide-gray-100'>
						{close.control.map(c => (
							<tr key={c.line} className={c.negative_balance ? 'bg-red-50/40' : ''}>
								<td className='px-3 py-2.5 font-medium text-gray-800'>
									{LINE_LABEL[c.line] ?? c.line}
									{!c.account_exists && (
										<span className='ml-2 text-xs font-normal text-gray-400'>no account in Zoho</span>
									)}
								</td>
								<td className='px-3 py-2.5 text-right tabular-nums text-gray-600'>
									{c.account_exists ? inrFull(c.opening_rs) : '—'}
								</td>
								<td className='px-3 py-2.5 text-right tabular-nums text-gray-600'>
									{c.account_exists ? inrFull(c.provisioned_rs) : '—'}
								</td>
								<td className='px-3 py-2.5 text-right tabular-nums text-gray-600'>
									{c.account_exists ? inrFull(c.paid_rs) : '—'}
								</td>
								<td
									className={`px-3 py-2.5 text-right tabular-nums font-semibold ${
										c.negative_balance ? 'text-red-700' : 'text-gray-900'
									}`}>
									{c.account_exists ? inrFull(c.closing_rs) : '—'}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{close.control.some(c => c.negative_balance) && (
				<div className='flex items-start gap-2 mt-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2'>
					<AlertTriangle className='w-4 h-4 mt-0.5 shrink-0' />
					<span>
						<span className='font-medium'>A liability account is in debit.</span> More has been paid out
						than was ever provided. The cause is known: ₹2,11,336 of PF was booked here instead of
						PF payable A/c — ₹84,757 for May wages (A2) and ₹1,26,579 for June (A3), both confirmed
						paid to EPFO. Reclass them and this account closes at −₹3,192 instead.
					</span>
				</div>
			)}
		</section>

		<section>
			<div className='flex items-baseline justify-between mb-2'>
				<h3 className='text-sm font-medium text-gray-700'>Monthly close</h3>
				<span className='text-xs text-gray-400'>
					row = wage month · its payment is read from the following month
				</span>
			</div>
			<div className='overflow-x-auto border border-gray-200 rounded-lg'>
				<table className='w-full text-sm'>
					<thead className='bg-gray-50 text-gray-500'>
						<tr>
							<th className='text-left px-3 py-2 font-medium'>Wage month</th>
							{close.control.map(c => (
								<th key={c.line} className='text-left px-3 py-2 font-medium'>
									{LINE_LABEL[c.line] ?? c.line}
								</th>
							))}
						</tr>
					</thead>
					<tbody className='divide-y divide-gray-100'>
						{close.grid.map(row => (
							<tr key={row.wage_month} className='hover:bg-gray-50'>
								<td className='px-3 py-2 whitespace-nowrap'>
									<span className='text-gray-800'>{row.wage_month}</span>
									<span className='text-xs text-gray-400 ml-2'>paid {row.payment_month}</span>
								</td>
								{row.cells.map(cell => {
									const v = CELL[cell.status] ?? CELL.no_activity;
									return (
										<td key={cell.line} className='px-3 py-2'>
											<div
												title={[
													v.title,
													cell.challan_rs != null ? `Challan ${inrFull(cell.challan_rs)}${cell.challan_ref ? ` · ref ${cell.challan_ref}` : ''}` : null,
													cell.challan_paid_on ? `Department paid ${cell.challan_paid_on}` : null,
													cell.provisioned_rs !== null
														? `Booked here: provisioned ${inrFull(cell.provisioned_rs)} · paid ${inrFull(cell.paid_rs)}`
														: null,
												]
													.filter(Boolean)
													.join('\n')}
												className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs ${v.cls}`}>
												<span>{v.label}</span>
												{cell.findings.map(f => (
													<button
														key={f.id}
														onClick={() => onFinding(f.id)}
														className='font-mono font-semibold underline decoration-dotted hover:no-underline'>
														{f.code}
													</button>
												))}
											</div>
											{(cell.status === 'differs' || cell.status === 'no_challan') && cell.diff_rs !== null && Math.abs(cell.diff_rs) > 1 && (
												<div className='text-[11px] text-gray-400 mt-0.5 tabular-nums'>
													{inrFull(Math.abs(cell.diff_rs))} apart
												</div>
											)}
										</td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<div className='flex flex-wrap gap-3 mt-2 text-xs text-gray-500'>
				{['tied', 'attention', 'differs', 'paid_elsewhere', 'paid_late', 'unpaid', 'no_challan', 'no_activity'].map(k => (
					<span key={k} className='inline-flex items-center gap-1'>
						<span className={`inline-block rounded border px-1.5 ${CELL[k].cls}`}>{CELL[k].label}</span>
						{CELL[k].title}
					</span>
				))}
			</div>
			<p className='text-xs text-gray-400 mt-2'>
				Only the Zoho leg is loaded, so a “tied” cell means the books agree with themselves — not that
				the challan, the return and the salary sheet agree with them.
			</p>
		</section>
	</div>
);

export default StatutoryCloseView;
